import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetResourcesResponse, GetStepsResponse, ResourceType } from '@/services/api/api.types'

export type StepWithValidation = GetStepsResponse[0] & {
  valid: boolean
  contentType?: ResourceType
  resources?: Array<GetResourcesResponse[0] & { valid: boolean }>
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
}

export interface StepsWithValidationResult {
  data: StepWithValidation[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  isSuccess: boolean
  refetch: () => Promise<{ data?: StepWithValidation[] }>
}

/**
 * Custom hook that calculates step validation status by batching resource queries.
 * Uses React Query's useQueries to fetch resources for all steps in parallel with caching.
 */
export function useStepsWithValidation(stepsQuery: {
  data?: GetStepsResponse
  isSuccess: boolean
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<{ data?: GetStepsResponse }>
}): StepsWithValidationResult {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()

  // Get the steps data
  const steps = stepsQuery.data ?? []

  // Fetch all resources (including source files) directly from endpoint
  // Note: api.getResources filters out source files, but we need them for contentType
  const resourceQueries = useQueries({
    queries: steps.map(step => ({
      queryKey: ['step', 'resources', 'all', step.stepId],
      queryFn: async () => {
        const allResources = await api.request<GetResourcesResponse>('/cm/get_resources', 'get', {
          step_id: step.stepId,
        })

        // Download file data for supplemental resources with resourceKey (needed for validation)
        await Promise.all(
          allResources
            .filter(r => !r.isSourceFile && r.resourceKey)
            .map(async resource => {
              try {
                const file = await api.download({ file_path: resource.resourceKey as string })
                resource.data = file as string
              } catch (error) {
                console.error(
                  `[useStepsWithValidation] Failed to download file for resourceKey "${resource.resourceKey}":`,
                  error,
                )
                resource.data = ''
              }
            }),
        )

        return allResources
      },
      enabled: !!step.stepId && stepsQuery.isSuccess,
      staleTime: 5 * 60 * 1000, // 5 minutes - resources don't change frequently
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    })),
  })

  // Fetch content metadata per step for transcoding status
  const contentQueries = useQueries({
    queries: steps.map(step => ({
      queryKey: ['step', 'content', 'meta', step.stepId],
      queryFn: () => api.getContentMeta({ step_id: step.stepId }),
      enabled: !!step.stepId && stepsQuery.isSuccess,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchInterval: (query: { state: { data?: { transcodeCompleted?: boolean } } }) => {
        const data = query.state.data
        if (data && data.transcodeCompleted === false) {
          return 5000
        }
        return false
      },
    })),
  })

  // Compute the validation status
  const stepsWithValidation = useMemo(() => {
    if (!stepsQuery.isSuccess) {
      return []
    }

    return steps.map((step, index) => {
      const resourceQuery = resourceQueries[index]
      const contentQuery = contentQueries[index]

      const transcodeCompleted = contentQuery?.data?.transcodeCompleted
      const transcodeSuccess = contentQuery?.data?.transcodeSuccess

      // If resource query is still loading or failed, validate step name only
      // The validation is incomplete but we don't want to block the UI
      if (!resourceQuery?.isSuccess) {
        return {
          ...step,
          valid: !!step.name, // Basic validation - resources pending
          transcodeCompleted,
          transcodeSuccess,
        }
      }

      const allResources = resourceQuery.data ?? []
      const sourceFile = allResources.find(r => r.isSourceFile)
      const supplementalResources = allResources.filter(r => !r.isSourceFile)
      const resourcesWithValid = supplementalResources.map(r => ({
        ...r,
        valid: !!r.title && !!r.description && !!r.data,
      }))
      const resourcesValid = resourcesWithValid.every(r => r.valid)

      return {
        ...step,
        valid: !!step.name && resourcesValid,
        contentType: sourceFile?.type,
        resources: resourcesWithValid,
        transcodeCompleted,
        transcodeSuccess,
      }
    })
  }, [steps, resourceQueries, contentQueries, stepsQuery.isSuccess])

  // Aggregate loading states and create unified result object
  const result = useMemo((): StepsWithValidationResult => {
    const isLoading = stepsQuery.isLoading
    const isFetching =
      stepsQuery.isFetching || resourceQueries.some(q => q.isFetching) || contentQueries.some(q => q.isFetching)
    const isError = stepsQuery.isError || resourceQueries.some(q => q.isError)
    const error = stepsQuery.error || resourceQueries.find(q => q.error)?.error || null
    // Success should only depend on steps being loaded, not resources
    const isSuccess = stepsQuery.isSuccess

    return {
      data: stepsWithValidation,
      isLoading,
      isFetching,
      isError,
      error,
      isSuccess,
      refetch: async () => {
        // Refetch steps and invalidate all step resource and content queries
        const stepsResult = await stepsQuery.refetch()

        steps.forEach(step => {
          queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', step.stepId] })
          queryClient.invalidateQueries({ queryKey: ['step', 'content', 'meta', step.stepId] })
        })

        // Return the data with validation
        if (stepsResult.data) {
          const enhancedSteps = stepsResult.data.map(step => ({
            ...step,
            valid: !!step.name, // Basic validation until resources are revalidated
          }))
          return { data: enhancedSteps }
        }

        return { data: undefined }
      },
    }
  }, [stepsQuery, resourceQueries, contentQueries, stepsWithValidation, queryClient, steps])

  return result
}
